import { path } from 'utils'
import { z } from 'zod'

import { ExternalAuthorization, OperationLevelPermission } from '../auth-claims/permissions'
import { featureCollectionSchema, featureSchema, pointSchema } from '../geojson-schemas'
import { defineApi } from '../http'
import { SharingConnectionType } from '../partner-access/connection-types'
import { manyItems } from '../shared'

/**
 * The analytes a dataset can report. Senders on the GeoJSON path must use these,
 * so adding a lab's exotic measurement means adding a member here rather than
 * carrying an uninterpretable column.
 */
export enum SoilAnalyte {
  Ph = 'ph',
  BufferPh = 'buffer-ph',
  OrganicMatter = 'organic-matter',
  Phosphorus = 'phosphorus',
  Potassium = 'potassium',
  Magnesium = 'magnesium',
  Calcium = 'calcium',
  Sodium = 'sodium',
  Sulfur = 'sulfur',
  Nitrate = 'nitrate',
  Ammonium = 'ammonium',
  Zinc = 'zinc',
  Manganese = 'manganese',
  Iron = 'iron',
  Copper = 'copper',
  Boron = 'boron',
  Chloride = 'chloride',
  Cec = 'cec',
  BaseSaturation = 'base-saturation',
  CalciumSaturation = 'calcium-saturation',
  MagnesiumSaturation = 'magnesium-saturation',
  PotassiumSaturation = 'potassium-saturation',
  HydrogenSaturation = 'hydrogen-saturation',
  SodiumSaturation = 'sodium-saturation',
  SolubleSalts = 'soluble-salts',
  ExcessLime = 'excess-lime'
}

/**
 * The soil extraction a result was produced by. Values from different
 * extractions are not comparable, so two descriptors that resolve to the same
 * {@link SoilAnalyte} under different methods stay separate map layers.
 */
export enum ExtractionMethod {
  Bray1 = 'bray-1',
  Bray2 = 'bray-2',
  Olsen = 'olsen',
  Mehlich3 = 'mehlich-3',
  AmmoniumAcetate = 'ammonium-acetate'
}

export enum SoilAnalyteUnit {
  Ppm = 'ppm',
  Percent = 'percent',
  MeqPer100g = 'meq/100g',
  MmhosPerCm = 'mmhos/cm',
  LbsPerAcre = 'lbs/ac',
  KgPerHectare = 'kg/ha'
}

export enum DepthUnit {
  Inches = 'in',
  Centimeters = 'cm'
}

/**
 * Whether a sample covers a point or a zone. Point sampling is all we ingest
 * today, but every dataset declares which it is so that adding zone sampling
 * does not mean backfilling what came before.
 */
export enum SoilSamplingType {
  Point = 'point',
  Zone = 'zone'
}

export enum SoilSampleSource {
  Partner = 'partner',
  Upload = 'upload'
}

export const soilSampleDepthSchema = z
  .object({
    top: z.number().nonnegative(),
    bottom: z.number().positive(),
    unit: z.enum(DepthUnit)
  })
  .refine(({ top, bottom }) => bottom > top, {
    message: 'Depth bottom must be below top'
  })

/**
 * What a sender declares about one column of results.
 *
 * This is the canonical path: analyte, unit, and method are our enums, so an
 * unrecognized value is a rejected request rather than an unlabelled layer.
 * Labs do not emit GeoJSON, so everyone on this path is building against our
 * spec, and flexibility here buys them confusion rather than tolerance. The
 * fault-tolerant resolution belongs to file upload, where the labels are
 * whatever a lab chose to print.
 *
 * `key` is a payload-local join key between this declaration and the values on
 * each feature. It carries no meaning, and exists so one dataset can report the
 * same analyte under two extractions.
 */
export const analyteDeclarationSchema = z.object({
  key: z.string().min(1),
  analyte: z.enum(SoilAnalyte),
  unit: z.enum(SoilAnalyteUnit).nullable(),
  method: z.enum(ExtractionMethod).nullable()
})

/**
 * A sample carries nothing but where it was taken and what was measured there.
 * Points are never addressed individually — the document is always read whole,
 * and interpolation works from coordinates — so they hold no id of their own.
 */
export const soilSamplePointSchema = featureSchema({
  geometry: pointSchema,
  properties: z.object({
    /**
     * Keyed by {@link analyteDeclarationSchema}'s `key`. An analyte not measured
     * at this point is omitted: there is no sentinel for absence, and zero means
     * zero.
     */
    values: z.record(z.string(), z.number())
  })
})

export const soilSampleCollectionSchema = featureCollectionSchema(soilSamplePointSchema)

export const importSoilSamplesSchema = z.object({
  samplingEvent: z.object({
    /**
     * The sender's identifier for the sampling activity. Together with the
     * partner and depth it derives the dataset id, so re-sending an activity
     * overwrites it rather than adding a second copy. Not stored: the lookup it
     * would serve runs the other way, from a partner's id to ours.
     */
    externalId: z.string().min(1),
    /**
     * The season the samples belong to. Required because it groups the map
     * layers, and a sender that cannot date a sampling can still say which
     * season it was.
     */
    season: z.number().int(),
    collectedOn: z.iso.date().nullable(),
    analyzedOn: z.iso.date().optional(),
    labName: z.string().optional(),
    depth: soilSampleDepthSchema.optional()
  }),
  analytes: z
    .array(analyteDeclarationSchema)
    .min(1)
    .refine(
      (analytes) => new Set(analytes.map(({ key }) => key)).size === analytes.length,
      'Analyte keys must be unique within a request'
    ),
  collections: soilSampleCollectionSchema
})

export const soilSampleIngestReportSchema = z.object({
  datasetId: z.string(),
  season: z.number().int(),
  samplesAccepted: z.number().int(),
  samplesDroppedEmpty: z.number().int(),
  analytesAccepted: z.array(z.string()),
  analytesPruned: z.array(z.string()),
  analytesClipped: z.array(z.string())
})

/**
 * A declaration as stored. Identical to what a sender declares, because the
 * GeoJSON path is canonical and nothing is reinterpreted on the way in.
 */
export const analyteDescriptorSchema = analyteDeclarationSchema

export const soilSampleSourceSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(SoilSampleSource.Partner),
    /**
     * The partner's Cognito username, as verified by the gateway authorizer
     * rather than asserted by the caller.
     */
    partnerId: z.string()
  }),
  z.object({
    type: z.literal(SoilSampleSource.Upload),
    username: z.string(),
    fileName: z.string()
  })
])

/**
 * One field's results from one sampling event at one depth. The points live in
 * S3; this is the record that makes them listable and renderable.
 */
export const soilSamplingDatasetSchema = z.object({
  orgCode: z.string(),
  id: z.string(),
  fieldId: z.string(),
  season: z.number().int(),
  collectedOn: z.iso.date().nullable(),
  analyzedOn: z.iso.date().optional(),
  labName: z.string().optional(),
  depth: soilSampleDepthSchema.optional(),
  source: soilSampleSourceSchema,
  analytes: z.array(analyteDescriptorSchema),
  samplingType: z.enum(SoilSamplingType),
  sampleCount: z.number().int(),
  createdAt: z.iso.datetime()
})

/**
 * Hoists the sampling type out of the dataset so a client narrows `collections`
 * by checking one discriminant. Zone sampling adds a member here rather than
 * widening `collections` into a union nothing can narrow.
 */
export const soilSamplingDatasetDetailSchema = z.discriminatedUnion('samplingType', [
  z.object({
    samplingType: z.literal(SoilSamplingType.Point),
    dataset: soilSamplingDatasetSchema.omit({ samplingType: true }),
    collections: soilSampleCollectionSchema
  })
])

const baseSoilSamplePath = ({ orgCode, fieldId }: { orgCode: string; fieldId: string }) =>
  `/locutus/${orgCode}/soil-samples/${fieldId}`

/**
 * Partner-only: there is no Panorama-client import path, so no Panorama user
 * permission applies. Access is gated by the partner-access authorizer at the
 * gateway, which also supplies the partner identity.
 */
export const importSoilSamplesForPartnerApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    fieldId: z.string()
  }),
  pathFor: baseSoilSamplePath,
  name: 'ImportSoilSamplesForPartner',
  description: 'Import soil sample results for a field from a partner integration',
  requiredPermissions: ExternalAuthorization.NonPanoramaCaller,
  method: 'POST',
  requestBodySchema: importSoilSamplesSchema,
  responseSchema: soilSampleIngestReportSchema,
  gatewayPath: '/import/soil-samples/:orgCode/:fieldId' as const,
  connectionTypes: [SharingConnectionType.AgronomicAdvisor]
})

/**
 * The map's soil layer list for a field. Descriptors travel with each dataset so
 * the layer list can be built without fetching any points.
 */
export const getSoilSampleDatasetsApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    fieldId: z.string()
  }),
  querySchema: z.object({
    season: z.coerce.number().int().optional()
  }),
  pathFor: ({ orgCode, fieldId, season }) =>
    path(baseSoilSamplePath({ orgCode, fieldId }), { season }),
  name: 'GetSoilSampleDatasets',
  description:
    'List soil sample datasets for a field, without point data, optionally filtered to one season',
  requiredPermissions: OperationLevelPermission.ViewPlans,
  responseSchema: manyItems(soilSamplingDatasetSchema),
  queryKey: ({ orgCode, fieldId, season }) => [orgCode, 'soil-samples', { fieldId, season }],
  gatewayPath: '/:orgCode/soil-samples/:fieldId' as const
})

export const getSoilSampleDatasetApi = defineApi({
  pathSchema: z.object({
    orgCode: z.string(),
    fieldId: z.string(),
    datasetId: z.string()
  }),
  pathFor: ({ orgCode, fieldId, datasetId }) =>
    `${baseSoilSamplePath({ orgCode, fieldId })}/${datasetId}`,
  name: 'GetSoilSampleDataset',
  description: 'Retrieve one soil sample dataset with its points',
  requiredPermissions: OperationLevelPermission.ViewPlans,
  responseSchema: soilSamplingDatasetDetailSchema,
  queryKey: ({ orgCode, fieldId, datasetId }) => [orgCode, 'soil-samples', { fieldId, datasetId }],
  gatewayPath: '/:orgCode/soil-samples/:fieldId/:datasetId' as const
})

export namespace SoilSampling {
  export type Dataset = z.infer<typeof soilSamplingDatasetSchema>
  export type AnalyteDescriptor = z.infer<typeof analyteDescriptorSchema>
  export type AnalyteDeclaration = z.infer<typeof analyteDeclarationSchema>
  export type Collection = z.infer<typeof soilSampleCollectionSchema>
  export type ImportRequest = z.infer<typeof importSoilSamplesSchema>
  export type IngestReport = z.infer<typeof soilSampleIngestReportSchema>
}
